import React from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import FileUpload from '../FileUpload';
import { useTranslation } from 'react-i18next';

const SellerMediaTab = ({ activeBooth, setActiveBooth, fetchData }) => {
  const { t } = useTranslation();

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Logo & Banner */}
      <Card>
        <CardHeader>
          <CardTitle>{t('dashboard.booth_branding')}</CardTitle>
          <CardDescription>{t('dashboard.booth_branding_desc')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label className="mb-2 block">{t('dashboard.logo')}</Label>
            <p className="text-xs text-slate-500 mb-2">{t('dashboard.logo_desc')}</p>
            <FileUpload
              entityType="booth" entityId={activeBooth?.id} mediaType="logo"
              currentUrl={activeBooth?.logo_url}
              onUploadSuccess={(url) => { setActiveBooth(prev => ({ ...prev, logo_url: url })); fetchData(); }}
              onRemove={async () => {
                try { await axios.put(`${API}/booths/${activeBooth?.id}`, { logo_url: null }); setActiveBooth(prev => ({ ...prev, logo_url: null })); }
                catch (e) { console.error('Failed to remove logo:', e); }
              }}
              accept="image/*" label={t('dashboard.upload_logo')} previewClassName="max-w-[200px] max-h-[150px] min-h-[80px]"
            />
          </div>
          <div>
            <Label className="mb-2 block">{t('dashboard.banner')}</Label>
            <p className="text-xs text-slate-500 mb-2">{t('dashboard.banner_desc')}</p>
            <FileUpload
              entityType="booth" entityId={activeBooth?.id} mediaType="banner"
              currentUrl={activeBooth?.banner_url}
              onUploadSuccess={(url) => { setActiveBooth(prev => ({ ...prev, banner_url: url })); fetchData(); }}
              onRemove={async () => {
                try { await axios.put(`${API}/booths/${activeBooth?.id}`, { banner_url: null }); setActiveBooth(prev => ({ ...prev, banner_url: null })); }
                catch (e) { console.error('Failed to remove banner:', e); }
              }}
              accept="image/*" label={t('dashboard.upload_banner')} previewClassName="w-full aspect-[4/1]" fillWidth={true}
            />
          </div>
        </CardContent>
      </Card>

      {/* Gallery & Videos */}
      <Card>
        <CardHeader>
          <CardTitle>{t('dashboard.gallery_videos')}</CardTitle>
          <CardDescription>{t('dashboard.gallery_desc')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Image Gallery */}
          <div>
            <Label className="mb-2 block">{t('dashboard.images')}</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {(activeBooth?.media || []).filter(m => m.type === 'image').map((media) => (
                <div key={media.id} className="relative group aspect-square bg-gradient-to-b from-slate-50 to-slate-100 rounded-lg overflow-hidden flex items-center justify-center">
                  <img src={media.url.startsWith('/api') ? `${API}${media.url.replace('/api', '')}` : media.url} alt={t('dashboard.gallery')} className="max-w-full max-h-full object-contain" />
                  <button onClick={async () => { await axios.delete(`${API}/upload/booth/${activeBooth?.id}/media/${media.id}`); fetchData(); toast.success('Image deleted'); }}
                    className="absolute top-1 right-1 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-sm">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
              <FileUpload entityType="booth" entityId={activeBooth?.id} mediaType="image" onUploadSuccess={() => fetchData()}
                accept="image/*" label="Add Images" previewClassName="aspect-square" showPreview={false} multiple={true} />
            </div>
          </div>

          {/* Video Gallery */}
          <div>
            <Label className="mb-2 block">{t('dashboard.videos')}</Label>
            <p className="text-xs text-slate-500 mb-2">{t('dashboard.videos_desc')}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {(activeBooth?.media || []).filter(m => m.type === 'video').map((media) => (
                <div key={media.id} className="relative group aspect-video bg-slate-900 rounded-lg overflow-hidden">
                  <video src={media.url.startsWith('/api') ? `${API}${media.url.replace('/api', '')}` : media.url} className="w-full h-full object-contain" controls />
                  <button onClick={async () => { await axios.delete(`${API}/upload/booth/${activeBooth?.id}/media/${media.id}`); fetchData(); toast.success('Video deleted'); }}
                    className="absolute top-1 right-1 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-sm">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
              <FileUpload entityType="booth" entityId={activeBooth?.id} mediaType="video" onUploadSuccess={() => fetchData()}
                accept="video/*" label={t('dashboard.upload_video')} previewClassName="aspect-video" showPreview={false} multiple={true} maxSize={50} />
            </div>
          </div>

          {/* PDF Documents */}
          <div>
            <Label className="mb-2 block">{t('dashboard.brochures')}</Label>
            <p className="text-xs text-slate-500 mb-2">{t('dashboard.brochures_desc')}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {(activeBooth?.media || []).filter(m => m.type === 'pdf').map((media) => (
                <div key={media.id} className="relative group bg-white border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <a href={media.url.startsWith('/api') ? `${API}${media.url.replace('/api', '')}` : media.url} target="_blank" rel="noopener noreferrer" className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-12 h-16 bg-red-50 border border-red-100 rounded flex items-center justify-center">
                      <svg className="w-8 h-8 text-red-500" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20M10.92,12.31C10.68,11.54 10.15,9.08 11.55,9.04C12.95,9 12.03,12.16 12.03,12.16C12.42,13.65 14.05,14.72 14.05,14.72C14.55,14.57 17.4,14.24 17,15.72C16.57,17.2 13.5,15.81 13.5,15.81C11.55,15.95 10.09,16.47 10.09,16.47C8.96,18.58 7.64,19.5 7.1,18.61C6.43,17.5 9.23,16.07 9.23,16.07C10.68,13.72 10.9,12.35 10.92,12.31Z"/>
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{media.filename || 'Document.pdf'}</p>
                      <p className="text-xs text-slate-500">Click to view</p>
                    </div>
                  </a>
                  <button onClick={async (e) => { e.preventDefault(); await axios.delete(`${API}/upload/booth/${activeBooth?.id}/media/${media.id}`); fetchData(); toast.success('Document deleted'); }}
                    className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-sm">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
              <FileUpload entityType="booth" entityId={activeBooth?.id} mediaType="pdf" onUploadSuccess={() => fetchData()}
                accept="application/pdf" label="Add PDF" previewClassName="h-24" showPreview={false} multiple={true} maxSize={20} />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SellerMediaTab;
